import { useState } from "react";
import toast from "react-hot-toast";
import { issuedLetterService } from "../../../services/api";
import { DsButton } from "../../../design-system";

/**
 * ESS-only: request OTP to personal email, verify, stamp PDF server-side.
 */
export default function IssuedLetterSignModal({ open, letter, onClose, onSigned }) {
  const [step, setStep] = useState("confirm"); // confirm | otp
  const [otp, setOtp] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !letter) return null;

  const letterLabel = letter.letterName || letter.letterType || "Letter";

  const handleRequestOtp = async () => {
    setBusy(true);
    try {
      const res = await issuedLetterService.requestSignOtp(letter.id);
      if (res?.success === false) {
        throw new Error(res?.message || "Failed to send OTP");
      }
      setMaskedEmail(res?.maskedEmail || "");
      setStep("otp");
      toast.success(res?.message || "OTP sent to your personal email");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to send OTP");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    const code = String(otp || "").trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setBusy(true);
    try {
      const res = await issuedLetterService.verifySignOtp(letter.id, code);
      if (res?.success === false) {
        throw new Error(res?.message || "OTP verification failed");
      }
      toast.success("Document signed successfully");
      onSigned?.(res?.data || letter);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-xl">
        <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">Sign document</h3>
        <p className="mt-1 text-sm text-[hsl(var(--ds-muted-fg))]">{letterLabel}</p>

        {step === "confirm" ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-[hsl(var(--ds-fg))]">
              An OTP will be sent to your personal email. After verification, a signature stamp is
              added to the PDF and status becomes Signed through workflow.
            </p>
            <div className="flex justify-end gap-2">
              <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </DsButton>
              <DsButton type="button" onClick={handleRequestOtp} disabled={busy}>
                {busy ? "Sending…" : "Send OTP"}
              </DsButton>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              Enter the OTP sent to {maskedEmail || "your personal email"}.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full rounded-md border border-[hsl(var(--ds-border))] bg-transparent px-3 py-2 text-center text-lg tracking-[0.35em]"
              placeholder="••••••"
              autoFocus
            />
            <div className="flex justify-between gap-2">
              <DsButton
                type="button"
                variant="secondary"
                onClick={handleRequestOtp}
                disabled={busy}
              >
                Resend OTP
              </DsButton>
              <div className="flex gap-2">
                <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </DsButton>
                <DsButton type="button" onClick={handleVerify} disabled={busy || otp.length !== 6}>
                  {busy ? "Verifying…" : "Verify & Sign"}
                </DsButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
